import { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ProgressIndicator } from './progress'
import { Accordion, AccordionItem, ContestSelection } from 'components/Accordion'
import './style.css'
import { useContests } from 'pages/Planning/hooks/useContests'
import { useDisciplines } from 'pages/Planning/hooks/useDisciplines'
import { useWeeklyStudy } from 'pages/Planning/hooks/useWeeklyStudy'
import { useConfigurationData } from 'pages/Planning/hooks/useConfigurationData'
import { IContestOption } from './contests/ContestSelection'
import DisciplinesSelection from './disciplines/DisciplinesSelection'
import WeeklyStudyLoad from './weekly-schedule'
import ConfigurationSummary from './summary/ConfigurationSummary'
import { RootState } from 'store/modules/rootReducer'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { addPlan, updatePlan } from 'store/modules/studyPlan/actions'
import { IoIosArrowBack } from 'react-icons/io'
import { AiOutlineDelete } from 'react-icons/ai'
import { BiHelpCircle } from 'react-icons/bi'
import Modal from 'components/Modal'
import { planningApi } from 'pages/Planning/services/api'
import { toast } from 'react-toastify'
import { useTimerGuard } from '../../../../hooks/useTimerGuard'
import BlockTimerModal from '../../../../components/Timer/BlockTimerModal'

interface ConfigurationContentProps {
  existingPlan?: IStudyPlan | null
  mode?: 'view' | 'create' | 'edit'
  onDelete?: (planId: string) => Promise<void>
  onStepChange?: (step: number) => void
  wizardTour?: {
    startTourForStep: (step: 1 | 2 | 3 | 4, force?: boolean) => void
    skipTour: (step: 1 | 2 | 3 | 4) => void
  }
}

const ConfigurationContent: React.FC<ConfigurationContentProps> = ({
  existingPlan,
  mode = 'create',
  onDelete,
  onStepChange,
  wizardTour,
}) => {
  // Se está editando, abre na etapa 4 (resumo), senão abre na etapa 1
  const [expandedSteps, setExpandedSteps] = useState<string[]>(
    existingPlan ? ['step-4'] : ['step-1']
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingPlan, setIsLoadingPlan] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const accordionContainerRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const dispatch = useDispatch()
  const { isTimerActive, checkAndBlock } = useTimerGuard()

  const { isDarkMode } = useSelector((state: RootState) => state.theme)

  // Centralized configuration data management
  const {
    configurationData,
    updatePlanningName,
    updateContest,
    updateDisciplines,
    updateDisciplineSelection,
    updateKnowledgeLevel,
    updateRelevanceLevel,
    updateWeeklySchedule,
    getSelectedDisciplines,
    getConfigurationSummary,
    isContestSelected,
    isDisciplinesSelected,
    isWeeklyScheduleConfigured,
    isConfigurationComplete,
    getPlanningPayload,
  } = useConfigurationData()

  // Individual hooks for data fetching
  const { contests, loading, error, searchContests, search, loadMoreContests } = useContests()

  const { disciplines, loading: disciplinesLoading } = useDisciplines({
    contestId: configurationData.contest?._id || null,
  })

  const {
    weeklyHours,
    weeklySchedule,
    sessionDurationRange,
    organizationType,
    updateWeeklyHours,
    updateDaySelection,
    updateMinDuration,
    updateMaxDuration,
    updateOrganizationType,
    getTotalWeeklyHours,
    getSelectedDays,
    isStepCompleted: isWeeklyStepCompleted,
    validationErrors,
    hasValidationErrors,
    setSelectedDisciplinesCount,
  } = useWeeklyStudy()

  // Sync disciplines data with centralized state
  useEffect(() => {
    if (disciplines.length > 0) {
      updateDisciplines(disciplines)
    }
  }, [disciplines, updateDisciplines])

  // Sync weekly schedule data with centralized state
  useEffect(() => {
    const weeklyData = {
      weeklyHours,
      schedule: weeklySchedule,
      totalWeeklyHours: getTotalWeeklyHours(),
      selectedDays: getSelectedDays(),
      sessionDurationRange,
      organizationType,
    }
    updateWeeklySchedule(weeklyData)
  }, [
    weeklyHours,
    weeklySchedule,
    sessionDurationRange,
    organizationType,
    getTotalWeeklyHours,
    getSelectedDays,
    updateWeeklySchedule,
  ])

  // Sync selected disciplines count with validation hook
  useEffect(() => {
    const selectedCount = getSelectedDisciplines().length
    setSelectedDisciplinesCount(selectedCount)
  }, [configurationData.disciplines, getSelectedDisciplines, setSelectedDisciplinesCount])

  // Verificação no mount: bloquear acesso direto à criação/edição se timer ativo
  useEffect(() => {
    if ((mode === 'create' || mode === 'edit') && isTimerActive) {
      setIsBlockModalOpen(true)
    }
  }, [mode, isTimerActive])

  // Load existing plan data when editing
  useEffect(() => {
    const loadPlanData = async () => {
      if (!existingPlan) {
        return
      }

      setIsLoadingPlan(true)
      try {
        // 1. Carregar nome do plano
        if (existingPlan.name) {
          updatePlanningName(existingPlan.name)
        }

        // 2. Carregar contest selecionado
        if (existingPlan.publicTender) {
          const contestData = {
            _id: existingPlan.course?._id,
            publicTender: existingPlan.publicTender.name,
            icon: existingPlan.publicTender.icon,
            role: existingPlan.publicTender?.role || '',
            situation: '',
            career: existingPlan.publicTender?.career || '',
          }
          updateContest(contestData as any)
        }

        // 3. Carregar carga horária semanal
        if (existingPlan.config?.cycle?.hours) {
          updateWeeklyHours(existingPlan.config.cycle.hours)
        }

        // 4. Carregar dias de estudo
        if (existingPlan.config?.cycle?.weekdays) {
          existingPlan.config.cycle.weekdays.forEach((day) => {
            updateDaySelection(day as any, true)
          })
        }

        // 5. Carregar duração de sessão
        if (existingPlan.config?.cycle?.duration) {
          updateMinDuration(existingPlan.config.cycle.duration.min)
          updateMaxDuration(existingPlan.config.cycle.duration.max)
        }

        // NOTE: Step 6 (loading modules/disciplines) is handled in a separate useEffect
        // that waits for disciplines to be loaded. See below.

        // Abrir na etapa 4 (resumo)
        setExpandedSteps(['step-4'])
      } catch (error) {
        console.error('Error loading existing plan data:', error)
      } finally {
        setIsLoadingPlan(false)
      }
    }

    loadPlanData()
  }, [existingPlan?._id])

  // Separate effect to load modules once disciplines are available
  useEffect(() => {
    // Only proceed if we have an existing plan and disciplines are loaded
    if (!existingPlan || !existingPlan.modules || existingPlan.modules.length === 0) {
      return
    }

    // Wait for disciplines to be loaded (not loading and has data)
    if (disciplinesLoading || disciplines.length === 0) {
      return
    }

    // Process each module
    existingPlan.modules.forEach((module) => {
      // Selecionar a disciplina
      updateDisciplineSelection(module._id, true)

      // Atualizar nível de conhecimento (0 a 5)
      updateKnowledgeLevel(module._id, module.knowledge as any)

      // Atualizar nível de relevância (1 a 5)
      updateRelevanceLevel(module._id, module.relevance as any)
    })
  }, [
    existingPlan?._id,
    existingPlan?.modules,
    disciplines.length,
    disciplinesLoading,
    updateDisciplineSelection,
    updateKnowledgeLevel,
    updateRelevanceLevel,
  ])

  const contestOptions: IContestOption[] = contests.map((contest) => ({
    id: contest._id,
    name: contest.publicTender,
    role: contest.role,
    situation: contest.situation,
    career: contest.career,
    icon: contest.icon,
    isSelected: configurationData.contest?._id === contest._id,
  }))

  const handleContestSelect = (contestId: string) => {
    const selectedContest = contests.find((contest) => contest._id === contestId)
    if (selectedContest) {
      updateContest(selectedContest)
    }
  }

  const handleDisciplineSelection = (disciplineRef: string, isSelected: boolean) => {
    updateDisciplineSelection(disciplineRef, isSelected)
  }

  const handleKnowledgeLevelUpdate = (disciplineRef: string, knowledgeLevel: any) => {
    updateKnowledgeLevel(disciplineRef, knowledgeLevel)
  }

  const handleRelevanceLevelUpdate = (disciplineRef: string, relevanceLevel: any) => {
    updateRelevanceLevel(disciplineRef, relevanceLevel)
  }

  const handleNext = (nextStep: string) => {
    if (nextStep && canProceedToStep(nextStep)) {
      setExpandedSteps([nextStep])
    }
  }

  const handleStepToggle = (stepId: string) => {
    if (canProceedToStep(stepId)) {
      setExpandedSteps([stepId])
    }
  }

  const handleExpandedChange = (newExpandedSteps: string[]) => {
    setExpandedSteps(newExpandedSteps)
  }

  const canProceedToStep = (stepId: string): boolean => {
    switch (stepId) {
      case 'step-1':
        return true
      case 'step-2':
        return isContestSelected()
      case 'step-3':
        return isContestSelected() && isDisciplinesSelected()
      case 'step-4':
        return isContestSelected() && isDisciplinesSelected() && isWeeklyScheduleConfigured()
      default:
        return false
    }
  }

  const handleFinish = async () => {
    const action = async () => {
      try {
        setIsSubmitting(true)
        const payload = getPlanningPayload()

        let savedPlan: IStudyPlan

        if (mode === 'edit' && existingPlan?._id) {
          // Modo de edição: atualiza plano existente
          savedPlan = await planningApi.saveSchedule({
            ...payload,
            planId: existingPlan._id,
          })
          // Atualiza o plano no Redux (estado otimista)
          dispatch(updatePlan(savedPlan))
          toast.success('Plano de estudo atualizado com sucesso!')
        } else {
          // Modo de criação: gera novo plano
          savedPlan = await planningApi.generateSchedule(payload)
          // Adiciona o plano no Redux (estado otimista)
          dispatch(addPlan(savedPlan))
          toast.success('Plano de estudo criado com sucesso!')
        }

        // Redireciona para a página de planejamento após sucesso
        history.replace('/planejamento')
      } catch (error: any) {
        console.error('Error saving planning:', error)

        // Exibe mensagem de erro apropriada
        const errorMessage =
          error.response?.data?.message ||
          (mode === 'edit' ? 'Erro ao atualizar plano de estudo' : 'Erro ao criar plano de estudo')

        toast.error(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    await action()
  }

  const handleBack = () => {
    history.push('/planejamento')
  }

  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!existingPlan?._id || !onDelete) return

    const action = async () => {
      try {
        setIsDeleting(true)
        await onDelete(existingPlan._id)
        setShowDeleteModal(false)
        toast.success('Plano de estudo excluído com sucesso!')
        history.push('/planejamento')
      } catch (error) {
        console.error('Error deleting plan:', error)
        toast.error('Erro ao excluir plano')
      } finally {
        setIsDeleting(false)
      }
    }

    if (
      checkAndBlock(() => {
        setShowDeleteModal(false)
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    await action()
  }

  const handleCloseBlockModal = () => {
    setIsBlockModalOpen(false)
    setPendingAction(null)
    // Se estava em modo create/edit, redireciona para /planejamento
    if (mode === 'create' || mode === 'edit') {
      history.push('/planejamento')
    }
  }

  const handleFinishSession = () => {
    setIsBlockModalOpen(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
  }

  // Scroll to accordion container when an item is opened
  useEffect(() => {
    if (accordionContainerRef.current && expandedSteps.length > 0) {
      // Small delay to ensure the accordion content is rendered
      const timer = setTimeout(() => {
        accordionContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [expandedSteps])

  const getCurrentStep = () => {
    if (expandedSteps.includes('step-1')) return 1
    if (expandedSteps.includes('step-2')) return 2
    if (expandedSteps.includes('step-3')) return 3
    if (expandedSteps.includes('step-4')) return 4
    return 1
  }

  // Notify parent of step changes
  useEffect(() => {
    const step = getCurrentStep()
    if (onStepChange) {
      onStepChange(step)
    }
  }, [expandedSteps, onStepChange])

  // Notify parent of initial step when component mounts
  useEffect(() => {
    if (onStepChange) {
      const step = getCurrentStep()
      onStepChange(step)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const summary = getConfigurationSummary()

  // Se está carregando o plano, mostra loading
  if (isLoadingPlan) {
    return (
      <section className={`configuration-container ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="configuration-content">
          <div className="configuration-header">
            <div className="configuration-header-content">
              <h1 className="configuration-header-content-title">Carregando plano de estudos...</h1>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '2rem' }}>
            <div className="loading-block" style={{ width: '100%', height: 100 }} />
            <div className="loading-block" style={{ width: '100%', height: 100 }} />
            <div className="loading-block" style={{ width: '100%', height: 100 }} />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={`configuration-container ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="configuration-content">
        <div className="configuration-header">
          <div className="configuration-header-content">
            <div className="configuration-header-title-wrapper">
              {/* Botão voltar - só aparece em modo edição */}
              <button className="configuration-back-button" onClick={handleBack}>
                <IoIosArrowBack size={24} />
                <span>Voltar</span>
              </button>
              {/* Botão de ajuda */}
              {wizardTour && (
                <button
                  className="configuration-help-button"
                  onClick={() => {
                    const currentStep = getCurrentStep()
                    wizardTour.startTourForStep(currentStep as 1 | 2 | 3 | 4, true)
                  }}
                  title="Iniciar tour guiado"
                  aria-label="Iniciar tour guiado de criação do plano"
                >
                  <BiHelpCircle size={24} />
                </button>
              )}
            </div>

            <div className="configuration-header-title-wrapper" data-tour="wizard-header">
              <h1 className="configuration-header-content-title">
                {existingPlan ? 'Editar Planejo de Estudos' : 'Planejador de estudos'}
              </h1>
              <div className="configuration-header-title-actions">
                {/* Botão excluir - só aparece em modo edição */}
                {mode === 'edit' && existingPlan && (
                  <button className="configuration-delete-button" onClick={handleDeleteClick}>
                    <AiOutlineDelete size={20} />
                    <span>Excluir plano</span>
                  </button>
                )}
              </div>
            </div>

            <h2 className="configuration-header-content-subtitle">
              {existingPlan
                ? 'Atualize as configurações do seu plano de estudos'
                : 'Configure seu plano de estudos personalizado em apenas 4 passos'}
            </h2>
          </div>
        </div>
        <ProgressIndicator currentStep={getCurrentStep()} totalSteps={4} />
      </div>

      <div className="accordion-container" ref={accordionContainerRef}>
        <Accordion
          allowMultiple={false}
          expandedItems={expandedSteps}
          onExpandedChange={handleExpandedChange}
          isDarkMode={isDarkMode}
        >
          <AccordionItem
            id="step-1"
            title={
              <div className="accordion-title">
                <span>Concurso de interesse</span>
                <span className="accordion-subtitle">
                  {summary?.contest && <>{summary?.contest?.publicTender}</>}
                </span>
              </div>
            }
            stepNumber={1}
            isExpanded={expandedSteps.includes('step-1')}
            isCompleted={isContestSelected()}
            onToggle={handleStepToggle}
          >
            <ContestSelection
              options={contestOptions}
              selectedId={configurationData.contest?._id || null}
              onSelect={handleContestSelect}
              onNext={handleNext}
              loading={loading}
              searchValue={search}
              onSearch={searchContests}
              loadMore={loadMoreContests}
            />
          </AccordionItem>

          {isContestSelected() && (
            <AccordionItem
              id="step-2"
              title={
                <div className="accordion-title">
                  <span>Disciplinas</span>
                  <span className="accordion-subtitle">
                    {summary?.selectedDisciplines.length > 0 && (
                      <>
                        {summary?.selectedDisciplines.length}/{disciplines.length} disciplina
                        {summary?.selectedDisciplines.length > 1 ? 's' : ''} selecionada
                        {summary?.selectedDisciplines.length > 1 ? 's' : ''}
                      </>
                    )}
                  </span>
                </div>
              }
              stepNumber={2}
              isExpanded={expandedSteps.includes('step-2')}
              isCompleted={isDisciplinesSelected()}
              isDisabled={!isContestSelected()}
              onToggle={handleStepToggle}
            >
              <DisciplinesSelection
                options={disciplines}
                selectedItems={getSelectedDisciplines()}
                onSelect={handleDisciplineSelection}
                updateKnowledgeLevel={handleKnowledgeLevelUpdate}
                updateRelevanceLevel={handleRelevanceLevelUpdate}
                onNext={handleNext}
                isDarkMode={isDarkMode}
                loading={disciplinesLoading}
              />
            </AccordionItem>
          )}

          {isContestSelected() && isDisciplinesSelected() && (
            <AccordionItem
              id="step-3"
              title={
                <div className="accordion-title">
                  <span>Organização de estudos</span>
                  <span className="accordion-subtitle">
                    {summary?.weeklyHours > 0 && (
                      <>
                        {summary?.organizationType === 'cycle'
                          ? `Ciclo: ${summary?.weeklyHours}h/semana`
                          : `Cronograma: ${summary?.weeklyHours}h/semana em ${summary?.selectedDays.length} dias`}
                      </>
                    )}
                  </span>
                </div>
              }
              stepNumber={3}
              isExpanded={expandedSteps.includes('step-3')}
              isCompleted={isWeeklyScheduleConfigured()}
              isDisabled={!isContestSelected() || !isDisciplinesSelected()}
              onToggle={handleStepToggle}
            >
              <WeeklyStudyLoad
                onNext={handleNext}
                isDarkMode={isDarkMode}
                weeklyHours={weeklyHours}
                weeklySchedule={weeklySchedule}
                sessionDurationRange={sessionDurationRange}
                organizationType={organizationType}
                updateWeeklyHours={updateWeeklyHours}
                updateDaySelection={updateDaySelection}
                updateMinDuration={updateMinDuration}
                updateMaxDuration={updateMaxDuration}
                updateOrganizationType={updateOrganizationType}
                isStepCompleted={isWeeklyStepCompleted}
                validationErrors={validationErrors}
                hasValidationErrors={hasValidationErrors}
              />
            </AccordionItem>
          )}

          {isContestSelected() && isDisciplinesSelected() && isWeeklyScheduleConfigured() && (
            <AccordionItem
              id="step-4"
              title="Resumo da configuração"
              stepNumber={4}
              isExpanded={expandedSteps.includes('step-4')}
              isCompleted={isConfigurationComplete()}
              isDisabled={
                !isContestSelected() || !isDisciplinesSelected() || !isWeeklyScheduleConfigured()
              }
              onToggle={handleStepToggle}
            >
              <ConfigurationSummary
                summary={getConfigurationSummary()}
                onEdit={handleStepToggle}
                onFinish={handleFinish}
                isSubmitting={isSubmitting}
                isDarkMode={isDarkMode}
                onNameChange={updatePlanningName}
                mode={mode}
              />
            </AccordionItem>
          )}
        </Accordion>
      </div>

      {/* Modal de confirmação de exclusão */}
      <Modal open={showDeleteModal} onClose={handleCancelDelete}>
        <div className="delete-modal-content">
          <h2 className="delete-modal-title">Tem certeza disso?</h2>
          <p className="delete-modal-message">
            Essa ação não pode ser desfeita. O plano de estudos "{existingPlan?.name}" será excluído
            permanentemente.
          </p>
          <div className="delete-modal-actions">
            <button
              className="delete-modal-button delete-modal-button-cancel"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancelar
            </button>
            <button
              className="delete-modal-button delete-modal-button-confirm"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Sim, excluir plano'}
            </button>
          </div>
        </div>
      </Modal>

      <BlockTimerModal
        isOpen={isBlockModalOpen}
        onClose={handleCloseBlockModal}
        onFinish={handleFinishSession}
      />
    </section>
  )
}

export default ConfigurationContent
